import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Plus, X, ChevronRight, ShieldCheck, GitBranch, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { SectionHeader, DLLoading, EmptyState, DLButton, Slogan } from "@/components/dl/ui";
import { InactiveNotice } from "@/components/dl/EntitlementGate";

const PLAY_TYPES = ["strategy", "motion", "investigation", "concession", "other"];
const empty = { title: "", play_type: "strategy", decision: "", rationale: "", playbook_item_refs: [], case_record_refs: [], reason_for_change: "" };

export default function DLLockedPlays() {
  const { caseId, caseData, team } = useOutletContext();
  const [plays, setPlays] = useState(null);
  const [playbook, setPlaybook] = useState([]);
  const [records, setRecords] = useState([]);
  const [mode, setMode] = useState(null);          // "call" | "change"
  const [changeFrom, setChangeFrom] = useState(null);
  const [step, setStep] = useState("build");        // "build" | "preview"
  const [form, setForm] = useState(empty);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [justLocked, setJustLocked] = useState(null);

  const load = useCallback(async () => {
    if (!team) return;
    try {
      const [p, pb, rec] = await Promise.all([
        api.get(`/dl/teams/${team.dl_team_id}/locked-plays`),
        api.get(`/dl/teams/${team.dl_team_id}/playbook`),
        api.get(`/dl/cases/${caseId}/record`),
      ]);
      setPlays(p.data || []); setPlaybook(pb.data || []); setRecords(rec.data || []);
    } catch { setPlays([]); }
  }, [team, caseId]);

  useEffect(() => { load(); }, [load]);

  if (!team) return <InactiveNotice />;
  if (plays === null) return <DLLoading label="Opening the vault…" />;

  // Group into chains by root_play_id
  const chains = {};
  plays.forEach((p) => { const root = p.root_play_id || p.locked_play_id; (chains[root] = chains[root] || []).push(p); });
  Object.values(chains).forEach((c) => c.sort((a, b) => (a.strategy_sequence || 1) - (b.strategy_sequence || 1)));
  const chainList = Object.values(chains).sort((a, b) => (a[0].locked_at || "").localeCompare(b[0].locked_at || ""));

  const openCall = () => { setMode("call"); setChangeFrom(null); setStep("build"); setForm(empty); setPreview(null); };
  const openChange = (p) => { setMode("change"); setChangeFrom(p); setStep("build"); setPreview(null);
    setForm({ ...empty, title: p.title, play_type: p.play_type || "strategy", decision: p.decision, rationale: p.rationale }); };
  const close = () => { setMode(null); setChangeFrom(null); setForm(empty); setPreview(null); setStep("build"); };
  const toggle = (key, id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));

  const callPlay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dl/teams/${team.dl_team_id}/call-play`, {
        title: form.title, play_type: form.play_type, decision: form.decision, rationale: form.rationale,
        playbook_item_refs: form.playbook_item_refs, case_record_refs: form.case_record_refs,
      });
      setPreview(data.preview); setStep("preview");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const lockPlay = async () => {
    setBusy(true);
    const body = { title: form.title, play_type: form.play_type, decision: form.decision, rationale: form.rationale,
      playbook_item_refs: form.playbook_item_refs, case_record_refs: form.case_record_refs,
      idempotency_key: `lock_${team.dl_team_id}_${Date.now()}` };
    try {
      let res;
      if (mode === "change") res = await api.post(`/dl/locked-plays/${changeFrom.locked_play_id}/change-strategy`, { ...body, reason_for_change: form.reason_for_change });
      else res = await api.post(`/dl/teams/${team.dl_team_id}/lock-play`, body);
      setJustLocked(res.data); close(); await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <SectionHeader kicker="Locked Plays" title="Your committed calls."
        desc="Once locked, a play is permanent. If the case changes, you can call a Change of Strategy — but the original always stays on your record."
        right={<DLButton onClick={openCall} data-testid="dl-call-play-btn"><Plus className="w-4 h-4" /> Call the Play</DLButton>} />

      <div className="dl-panel-gold rounded-md px-4 py-3 flex items-center gap-3">
        <Lock className="w-5 h-5 text-gold shrink-0" />
        <span className="dl-slogan text-xs text-gold">If you didn't lock the play, you didn't call it.</span>
      </div>

      {chainList.length === 0 ? (
        <EmptyState icon={Lock} title="No plays locked yet" desc="Build your strategy in the Playbook, then call and lock your first play." testid="dl-plays-empty"
          action={<DLButton onClick={openCall}><Plus className="w-4 h-4" /> Call the Play</DLButton>} />
      ) : (
        <div className="space-y-5">
          {chainList.map((chain) => (
            <div key={chain[0].root_play_id} className="dl-panel rounded-lg p-4" data-testid="dl-play-chain">
              {chain.map((p, i) => (
                <div key={p.locked_play_id} className={i > 0 ? "mt-3 pt-3 border-t border-dashed border-white/10" : ""} data-testid="dl-play-card">
                  <div className="flex items-center gap-2 mb-1">
                    {i === 0 ? <span className="text-[10px] font-mono text-emerald-300 uppercase tracking-wider flex items-center gap-1"><Lock className="w-3 h-3" /> Original Play</span>
                      : <span className="text-[10px] font-mono text-gold uppercase tracking-wider flex items-center gap-1"><GitBranch className="w-3 h-3" /> Change #{p.strategy_sequence - 1}</span>}
                    <span className="text-[10px] font-mono text-white/30 ml-auto flex items-center gap-1"><Clock className="w-3 h-3" /> {(p.locked_at || "").replace("T", " ").slice(0, 16)}</span>
                  </div>
                  <div className="font-display font-bold text-white">{p.title} <span className="text-white/30 text-xs font-mono">· {p.play_type}</span></div>
                  <div className="mt-1 text-sm"><span className="text-white/40">Decision: </span><span className="text-white/80">{p.decision}</span></div>
                  <div className="mt-1 text-sm"><span className="text-white/40">Rationale: </span><span className="text-white/70">{p.rationale}</span></div>
                  {p.change_reason && <div className="mt-1 text-sm text-gold/80">Reason for change: {p.change_reason}</div>}
                  <div className="mt-2 flex flex-wrap gap-3 text-[11px] font-mono text-white/40">
                    <span>Cutoff: {p.record_cutoff ? String(p.record_cutoff).slice(0, 10) : "—"}</span>
                    <span>{(p.case_record_refs || []).length} record refs</span>
                    <span>{(p.playbook_item_refs || []).length} playbook refs</span>
                    <span>info universe: {p.available_case_record_count} records</span>
                  </div>
                  {(p.corrections || []).length > 0 && <div className="mt-1 text-[11px] font-mono text-red-300">{p.corrections.length} commissioner correction(s) on record</div>}
                </div>
              ))}
              <div className="mt-3">
                <DLButton variant="ghost" data-testid="dl-change-strategy-btn" onClick={() => openChange(chain[chain.length - 1])}><GitBranch className="w-3.5 h-3.5" /> Change of Strategy</DLButton>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Builder / preview / lock modal */}
      {mode && (
        <div className="fixed inset-0 z-[60] bg-black/75 flex items-center justify-center p-4" onClick={close}>
          <div className="dl-panel rounded-lg w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dl-play-builder">
            <div className="sticky top-0 bg-surface/95 backdrop-blur px-5 py-4 flex items-center justify-between border-b border-white/10">
              <Slogan>{mode === "change" ? "Change of Strategy" : "Call the Play"}{step === "preview" ? " · Review" : ""}</Slogan>
              <button onClick={close} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            {step === "build" && (
              <div className="p-5 space-y-3">
                {mode === "change" && (
                  <div className="dl-panel-gold rounded-md p-3 text-xs text-white/70">
                    <AlertTriangle className="w-4 h-4 text-gold inline mr-1" /> You are <b>not editing</b> the original play. This calls a new play; the original stays locked on your record.
                  </div>
                )}
                <div><label className="block text-xs text-white/50 mb-1">Play title</label>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="dl-play-title" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white focus:border-gold outline-none" /></div>
                <div><label className="block text-xs text-white/50 mb-1">Play type</label>
                  <select value={form.play_type} onChange={(e) => setForm({ ...form, play_type: e.target.value })} data-testid="dl-play-type" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white focus:border-gold outline-none">
                    {PLAY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
                <div><label className="block text-xs text-white/50 mb-1">Decision</label>
                  <textarea value={form.decision} onChange={(e) => setForm({ ...form, decision: e.target.value })} rows={2} data-testid="dl-play-decision" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white focus:border-gold outline-none" /></div>
                <div><label className="block text-xs text-white/50 mb-1">Rationale</label>
                  <textarea value={form.rationale} onChange={(e) => setForm({ ...form, rationale: e.target.value })} rows={3} data-testid="dl-play-rationale" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white focus:border-gold outline-none" /></div>
                {mode === "change" && (
                  <div><label className="block text-xs text-white/50 mb-1">Reason for change</label>
                    <textarea value={form.reason_for_change} onChange={(e) => setForm({ ...form, reason_for_change: e.target.value })} rows={2} data-testid="dl-play-change-reason" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2 text-white focus:border-gold outline-none" /></div>
                )}
                <div><label className="block text-xs text-white/50 mb-1">Playbook items relied upon</label>
                  <div className="max-h-32 overflow-y-auto border border-white/10 rounded-sm p-2">
                    {playbook.length === 0 ? <div className="text-xs text-white/30 py-1">No playbook items yet.</div> :
                      playbook.map((it) => <label key={it.item_id} className="flex items-center gap-2 py-1 text-sm text-white/70 cursor-pointer"><input type="checkbox" className="accent-gold" checked={form.playbook_item_refs.includes(it.item_id)} onChange={() => toggle("playbook_item_refs", it.item_id)} />{it.title} <span className="text-white/30 text-[10px]">({it.module})</span></label>)}
                  </div></div>
                <div><label className="block text-xs text-white/50 mb-1">Case Record material relied upon</label>
                  <div className="max-h-32 overflow-y-auto border border-white/10 rounded-sm p-2">
                    {records.length === 0 ? <div className="text-xs text-white/30 py-1">No admitted records.</div> :
                      records.map((r) => <label key={r.record_id} className="flex items-center gap-2 py-1 text-sm text-white/70 cursor-pointer"><input type="checkbox" className="accent-gold" checked={form.case_record_refs.includes(r.record_id)} onChange={() => toggle("case_record_refs", r.record_id)} />{r.title}</label>)}
                  </div></div>
                <div className="flex justify-end gap-2 pt-2">
                  <DLButton variant="ghost" onClick={close}>Cancel</DLButton>
                  <DLButton disabled={busy || !form.decision.trim() || !form.rationale.trim() || !form.title.trim() || (mode === "change" && !form.reason_for_change.trim())} onClick={callPlay} data-testid="dl-preview-play">
                    Call the Play <ChevronRight className="w-4 h-4" /></DLButton>
                </div>
              </div>
            )}

            {step === "preview" && preview && (
              <div className="p-5 space-y-4" data-testid="dl-play-preview">
                <div className="dl-panel-gold rounded-md p-4">
                  <div className="flex items-center gap-2 text-gold mb-2"><ShieldCheck className="w-5 h-5" /><span className="dl-slogan text-xs">Review before you lock</span></div>
                  <p className="text-sm text-white/70">You are committing this decision based on the information currently in the Case Record. Your original play will always remain in your Season Record. If new information changes your strategy later, you may call a Change of Strategy — the original stays locked.</p>
                </div>
                <div className="space-y-1 text-sm">
                  <div><span className="text-white/40">Title: </span><span className="text-white">{preview.title}</span></div>
                  <div><span className="text-white/40">Type: </span><span className="text-white">{preview.play_type}</span></div>
                  <div><span className="text-white/40">Decision: </span><span className="text-white">{preview.decision}</span></div>
                  <div><span className="text-white/40">Rationale: </span><span className="text-white/80">{preview.rationale}</span></div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="dl-panel rounded-sm py-2"><div className="label-mono text-[9px]">Record Cutoff</div><div className="text-white text-sm font-mono mt-1">{preview.record_cutoff ? String(preview.record_cutoff).slice(0, 10) : "—"}</div></div>
                  <div className="dl-panel rounded-sm py-2"><div className="label-mono text-[9px]">Record Refs</div><div className="text-white text-sm font-mono mt-1">{(preview.case_record_refs || []).length}</div></div>
                  <div className="dl-panel rounded-sm py-2"><div className="label-mono text-[9px]">Info Universe</div><div className="text-white text-sm font-mono mt-1">{preview.available_case_record_count}</div></div>
                </div>
                <div className="flex items-start gap-2 text-[11px] text-white/45"><AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" /> Locking is deliberate and permanent. Live with the consequences — no Monday-morning quarterbacking.</div>
                <div className="flex justify-between gap-2 pt-1">
                  <DLButton variant="ghost" onClick={() => setStep("build")}>Back</DLButton>
                  <DLButton disabled={busy} onClick={lockPlay} data-testid="dl-lock-play-confirm"><Lock className="w-4 h-4" /> Lock the Play</DLButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success state */}
      {justLocked && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={() => setJustLocked(null)}>
          <div className="dl-panel-gold rounded-lg w-full max-w-md p-8 text-center dl-lock-pulse" onClick={(e) => e.stopPropagation()} data-testid="dl-lock-success">
            <div className="w-16 h-16 rounded-full bg-gold/15 border border-gold/50 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8 text-gold" /></div>
            <div className="font-display font-black text-2xl text-white tracking-tight">PLAY LOCKED.</div>
            <div className="mt-3 space-y-1 text-sm font-mono text-white/60">
              <div>{(justLocked.locked_at || "").replace("T", " ").slice(0, 16)}</div>
              <div>Record cutoff: {justLocked.record_cutoff ? String(justLocked.record_cutoff).slice(0, 10) : "—"}</div>
              <div className="text-white/80 pt-1">{justLocked.decision}</div>
            </div>
            <p className="mt-3 text-[11px] text-white/40">Locking commits your decision — it does not mean it was legally correct.</p>
            <div className="mt-5"><DLButton className="w-full" onClick={() => setJustLocked(null)} data-testid="dl-lock-success-close">Back to Locked Plays</DLButton></div>
          </div>
        </div>
      )}
    </div>
  );
}
